// The only place this module talks to the CVCE pipeline API (cvce
// `api/routers/pipeline.py`, served at `/cvce/api/pipeline/*` and reached
// through `functions/cvce-pipeline/[[path]].ts`).
//
// The portal is not a backend (I-4): every rag colour, rank and count is
// CVCE's. This module formats; it never recomputes a colour, rank or count.
// Types mirror cvce `specs/pipeline-console-1-week-and-stage.md` field for
// field.

export type FreshnessMeta = {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
};

export type Enveloped<T> = { data: T; meta: FreshnessMeta };

// Same-origin via the Pages Function proxy.
const BASE = import.meta.env.VITE_CVCE_PIPELINE_API_BASE ?? "/cvce-pipeline/v1";

export class CvcePipelineError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`CVCE Pipeline API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** A GET whose body is whatever the endpoint sends: enveloped or not. */
export async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new CvcePipelineError(response.status, await safeBody(response));
  }
  return (await response.json()) as T;
}

/**
 * One message per API failure. `areaName` comes from the registry via the
 * shell (I-5), never a literal, so a 403 names the right area.
 */
export function messageFor(error: unknown, areaName: string, system = "CVCE"): string {
  if (!(error instanceof CvcePipelineError)) {
    return `We can't reach ${system} right now. Try again shortly.`;
  }
  switch (error.status) {
    case 401:
      return "Your session could not be confirmed. Reload the page to sign in again.";
    case 403:
      return `You do not have permission to see this. Ask the ${areaName} area owner.`;
    case 404:
      return "That stage does not exist.";
    case 422:
      return "That week or stage was not valid.";
    case 503: {
      const code =
        error.body !== null && typeof error.body === "object" ? (error.body as { error?: unknown }).error : null;
      return code === "not_configured"
        ? "The CVCE address is not configured on the portal, so nothing is shown."
        : `We can't reach ${system} right now. Try again shortly.`;
    }
    default:
      return `We can't reach ${system} right now. Try again shortly.`;
  }
}

// ---- /pipeline/weeks ----

export type PipelineWeek = { week_start: string; complete: boolean };

export type WeeksData = {
  available: boolean;
  weeks: PipelineWeek[];
  default_week: string | null;
};

export const getWeeks = () => getJson<Enveloped<WeeksData>>("/weeks");
export const weeksKey = ["cvce_pipeline", "weeks"] as const;

// ---- /pipeline/week ----

export type GreyReason = "no_feed" | "stale" | "no_data" | null;
/**
 * `low_rated_sample` (cvce `pipeline-console-4-rated-sample`, deploying
 * shortly): fewer than 3 rated samples fed the quality score — the tile
 * still carries a `quality_value`/`quality_rag`, but the reader is told the
 * number is too thin to judge, and every measure chip that reads it forces
 * grey regardless of what `quality_rag` itself says. See `measure.ts`
 * `qualityRag`/`tileNote` — never read `quality_rag` for the Q chip directly.
 */
export type QualityNote = "not_measured" | "low_sample" | "low_rated_sample" | null;
export type Rag = "red" | "amber" | "green" | "grey";
export type Lane = "frontdoor" | "cortex" | "protex" | "vox" | "outputs";
export type OutputRow = "cx" | "marketing" | "library" | null;
export type Grain = "week" | "month";

/** cvce `routers/pipeline.py` week tile — the row plus the display entry. */
export type PipelineTile = {
  stage_key: string;
  band: string;
  stage_order: number;
  step: number | null;
  label: string;
  lane: Lane | string;
  output_row: OutputRow | string;
  pending_on: string | null;
  n: number;
  throughput: number | null;
  quality_value: number | null;
  quality_stat: string | null;
  quality_note: QualityNote;
  /** How many rated samples fed `quality_value` — only meaningful with `quality_note: "low_rated_sample"`. */
  quality_n?: number | null;
  duration_p50_ms: number | null;
  rework_rate: number | null;
  quality_rag: Rag;
  time_rag: Rag;
  throughput_rag: Rag;
  rework_rag: Rag;
  overall_rag: Rag;
  last_event_at: string | null;
  low_sample: boolean;
  grey_reason: GreyReason;
};

export type FixFirstMode = "fix" | "watch" | "none";
export type FixFirst = { mode: FixFirstMode | string; items: string[] };

export type PipelineCounts = { red: number; amber: number; green: number; grey: number };

export type FrontDoors = { available: boolean; reason: string | null };

export type WeekData = {
  available: boolean;
  week_start: string | null;
  complete: boolean | null;
  tiles: PipelineTile[];
  fix_first: FixFirst;
  counts: PipelineCounts;
  front_doors: FrontDoors;
  last_event_at: string | null;
  /** Absent on a plain `week_start` request (grain defaults to "week" server-side either way). */
  grain?: Grain;
  period_start?: string | null;
};

export const getWeek = (weekStart: string) =>
  getJson<Enveloped<WeekData>>(`/week?week_start=${encodeURIComponent(weekStart)}`);
export const weekKey = (weekStart: string) => ["cvce_pipeline", "week", weekStart] as const;

/** The month-grain sibling of `getWeek` (cvce-pipeline-4). `grain: "week"` still works — it is `getWeek` in all but the cache key — but callers use `getWeek` for week so existing caches are untouched. */
export const getPeriodWeek = (grain: Grain, periodStart: string) => {
  const params = new URLSearchParams({ grain, period_start: periodStart });
  return getJson<Enveloped<WeekData>>(`/week?${params.toString()}`);
};
export const periodWeekKey = (grain: Grain, periodStart: string) =>
  ["cvce_pipeline", "week", grain, periodStart] as const;

// ---- /pipeline/periods ----

export type Period = { period_start: string; complete: boolean };
export type PeriodsData = { available: boolean; grain: Grain; periods: Period[]; default_period: string | null };

export const getPeriods = (grain: Grain) => getJson<Enveloped<PeriodsData>>(`/periods?grain=${grain}`);
export const periodsKey = (grain: Grain) => ["cvce_pipeline", "periods", grain] as const;

// ---- /pipeline/stages/{stage_key} ----

export type StageThresholds = {
  quality_stat: string | null;
  quality_green_min: number | null;
  quality_amber_min: number | null;
  time_green_max_hours: number | null;
  time_amber_max_hours: number | null;
  rework_green_max: number | null;
  rework_amber_max: number | null;
  throughput_amber_share: number | null;
};

export type StageWeekRow = {
  week_start: string;
  /** Set instead of/alongside `week_start` for a month-grain period row (cvce-pipeline-4). */
  period_start?: string;
  n: number;
  throughput: number | null;
  quality_value: number | null;
  /** See `PipelineTile.quality_note` — same "too few rated" contract, now also on a trend row. */
  quality_note?: QualityNote;
  quality_n?: number | null;
  duration_p50_ms: number | null;
  rework_rate: number | null;
  quality_rag: Rag;
  time_rag: Rag;
  throughput_rag: Rag;
  rework_rag: Rag;
  overall_rag: Rag;
  low_sample: boolean;
};

export type StageInfo = {
  stage_key: string;
  step: number | null;
  label: string;
  lane: Lane | string;
  output_row: OutputRow | string;
  pending_on: string | null;
  band: string;
  stage_order: number;
};

export type ThresholdStatus = "provisional" | string;

export type StageData = {
  available: boolean;
  stage: StageInfo | null;
  thresholds: StageThresholds | null;
  threshold_status: ThresholdStatus | null;
  weeks: StageWeekRow[];
  /** Month-grain sibling of `weeks` — present when `grain=month` was requested. */
  periods?: StageWeekRow[];
  grain?: Grain;
};

export const getStage = (stageKey: string, to?: string, weeks = 8) => {
  const params = new URLSearchParams({ weeks: String(weeks) });
  if (to) params.set("to", to);
  return getJson<Enveloped<StageData>>(`/stages/${encodeURIComponent(stageKey)}?${params.toString()}`);
};
export const stageKey = (stage: string, to: string | null, weeks: number) =>
  ["cvce_pipeline", "stage", stage, to ?? "default", weeks] as const;

/** The month-grain sibling of `getStage` (cvce-pipeline-4). */
export const getStagePeriod = (stageKeyValue: string, grain: Grain, to?: string, periods = 6) => {
  const params = new URLSearchParams({ grain, periods: String(periods) });
  if (to) params.set("to", to);
  return getJson<Enveloped<StageData>>(`/stages/${encodeURIComponent(stageKeyValue)}?${params.toString()}`);
};
export const stagePeriodKey = (stage: string, grain: Grain, to: string | null, periods: number) =>
  ["cvce_pipeline", "stage", stage, grain, to ?? "default", periods] as const;

// ---- /pipeline/findings ---- (cvce `specs/pipeline-console-2-findings-and-asset.md`)

export type FindingStatus = "open" | "resolved";

export type FindingRow = {
  finding_id: string;
  asset_id: string;
  claim: string;
  problem_type: string;
  origin_stage: string;
  blocking: boolean;
  status: FindingStatus;
  reviewed_at: string;
  reviewed_revision: string;
  newer_draft_since_review: boolean;
};

export type FindingsKpis = {
  findings_per_asset: number | null;
  findings_per_1000_words: number | null;
  findings_per_1000_words_note: "not_measured" | null;
  clean_review_rate: number | null;
  median_resolution_days: number | null;
  unattributed_share: number | null;
};

export type BiggestOpenGroup = { problem_type: string; origin_stage: string; n: number } | null;
export type FindingsMatrixCell = { problem_type: string; origin_stage: string; n: number };
/** `week_start` in place of `month` for a week-grain trend row (cvce-pipeline-4). */
export type FindingsTrendRow = { month?: string; week_start?: string; origin_stage: string; n: number };

export type FindingsData = {
  available: boolean;
  month: string | null;
  reviewed_assets: number;
  kpis: FindingsKpis;
  biggest_open_group: BiggestOpenGroup;
  matrix: FindingsMatrixCell[];
  trend: FindingsTrendRow[];
  rows: FindingRow[];
  /** cvce-pipeline-4: present once the week-grain option is deployed. */
  grain?: Grain;
  period_start?: string | null;
};

export type FindingsStatusFilter = "all" | "open" | "resolved";
export type FindingsBlockingFilter = "all" | "true" | "false";

export const getFindings = (month: string, status: FindingsStatusFilter, blocking: FindingsBlockingFilter) => {
  const params = new URLSearchParams({ month, status, blocking });
  return getJson<Enveloped<FindingsData>>(`/findings?${params.toString()}`);
};
export const findingsKey = (month: string, status: string, blocking: string) =>
  ["cvce_pipeline", "findings", month, status, blocking] as const;

/** The week-grain sibling of `getFindings` (cvce-pipeline-4 Acceptance 5). */
export const getFindingsForWeek = (
  weekStart: string,
  status: FindingsStatusFilter,
  blocking: FindingsBlockingFilter,
) => {
  const params = new URLSearchParams({ week_start: weekStart, status, blocking });
  return getJson<Enveloped<FindingsData>>(`/findings?${params.toString()}`);
};
export const findingsWeekKey = (weekStart: string, status: string, blocking: string) =>
  ["cvce_pipeline", "findings", "week", weekStart, status, blocking] as const;

// ---- /pipeline/assets/{asset_id} ----

export type AssetReview = {
  asset_id: string;
  kb_entry_id: string;
  reviewer: string;
  reviewed_at: string;
  reviewed_revision: string;
  finding_count: number;
} | null;

export type AssetFinding = {
  finding_id: string;
  asset_id: string;
  claim: string;
  source_says: string;
  source_kind: string;
  problem_type: string;
  origin_stage: string;
  problem_note: string | null;
  blocking: boolean;
  status: FindingStatus;
  reviewed_at: string;
  reviewed_revision: string;
  resolved_in_revision: string | null;
  resolved_at: string | null;
  newer_draft_since_review: boolean;
};

export type AssetEval = {
  draft_n: number;
  created_at: string;
  eval_verdict: string;
  score_overall: number | null;
  score_snippet_integrity: number | null;
  score_communication: number | null;
  score_kb_consistency: number | null;
};

export type AssetDetailData = {
  available: boolean;
  review: AssetReview;
  findings: AssetFinding[];
  evals: AssetEval[];
  latest_draft_at: string | null;
  newer_draft_since_review: boolean;
};

export const getAsset = (assetId: string) =>
  getJson<Enveloped<AssetDetailData>>(`/assets/${encodeURIComponent(assetId)}`);
export const assetKey = (assetId: string) => ["cvce_pipeline", "asset", assetId] as const;

// ---- /pipeline/friction ---- (cvce `specs/pipeline-friction-1-ledger-and-api.md`)

export type FrictionStage =
  | "cortex_research"
  | "kb_entry"
  | "blueprint"
  | "vox_draft"
  | "visuals"
  | "review"
  | "publish";

/** Pipeline order, the console's column order. CVCE's `friction_vocab.json` is the source. */
export const FRICTION_STAGES: FrictionStage[] = [
  "cortex_research",
  "kb_entry",
  "blueprint",
  "vox_draft",
  "visuals",
  "review",
  "publish",
];

export type FrictionDisposition = "attributed" | "excluded" | null;

export type FrictionRow = {
  friction_id: string;
  source: string;
  source_ref: string;
  subject_id: string | null;
  occurred_at: string;
  stage: FrictionStage | null;
  cause_class: string | null;
  detail: string;
  data_gap_field: string | null;
  link: string | null;
  channel: string | null;
  item_kind: string | null;
  attribution: string | null;
  disposition: FrictionDisposition;
  exclude_reason: string | null;
};

export type FrictionKpis = {
  total: number;
  attributed: number;
  excluded: number;
  unattributed: number;
  by_source: Record<string, number>;
  by_exclude_reason: Record<string, number>;
};

export type FrictionMatrixCell = { stage: FrictionStage | null; cause_class: string | null; n: number };

export type FrictionData = {
  available: boolean;
  rows: FrictionRow[];
  kpis: FrictionKpis;
  matrix: FrictionMatrixCell[];
};

export type FrictionGapStatus = "filled" | "explained" | "open";
export type FrictionGap = {
  gap_key: string;
  signal: string | number | null;
  status: FrictionGapStatus;
  why: string;
  rows_affected: number | null;
};
export type FrictionGapsData = { available: boolean; gaps: FrictionGap[] };

export const FRICTION_DAYS = [30, 60, 90, 180] as const;

export const getFriction = (days: number) =>
  getJson<Enveloped<FrictionData>>(`/friction?${new URLSearchParams({ days: String(days) }).toString()}`);
export const frictionKey = (days: number) => ["cvce_pipeline", "friction", days] as const;

export const getFrictionGaps = () => getJson<Enveloped<FrictionGapsData>>("/friction/gaps");
export const frictionGapsKey = ["cvce_pipeline", "friction", "gaps"] as const;

/** Same-origin download link through the Pages Function proxy; CVCE sets the filename. */
export const frictionExportUrl = (days: number, format: "csv" | "jsonl") =>
  `${BASE}/friction/export?${new URLSearchParams({ days: String(days), format }).toString()}`;
